import { useEffect } from "react";
import { useT } from "src/i18n";

// SPA routes all serve the same index.html, so the document <title>, meta
// description, and canonical link must be set client-side per route for the
// public, indexable pages. (Auth routes are robots-disallowed and only manage
// their own title.) Defaults mirror the static tags in index.html / the
// homepage, and are restored on unmount so nothing leaks onto a route that
// doesn't manage its own head.
const ORIGIN = "https://betonijerry.fi";

function upsertMeta(name, content) {
    let el = document.head.querySelector(`meta[name="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertCanonical(href) {
    let el = document.head.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function removeCanonical() {
    const el = document.head.querySelector('link[rel="canonical"]');
    if (el) el.remove();
}

/**
 * Set the per-route <title>, meta description, and canonical URL for an
 * indexable public page. Resets to the homepage defaults on unmount.
 *
 * @param {object} [opts]
 * @param {string} [opts.title]       full <title> text (defaults to the brand title)
 * @param {string} [opts.description] meta description (defaults to the homepage copy)
 * @param {string} [opts.path]        canonical path, e.g. "/tilaa" (defaults to "/")
 * @param {string} [opts.robots]      robots directive, e.g. "noindex,follow" (default: indexable).
 *                                     A noindex page emits no canonical (conflicting signals).
 */
export function usePageMeta({ title, description, path = "/", robots } = {}) {
    const t = useT();
    const defaultTitle = t("common.pageMeta.defaultTitle");
    const defaultDescription = t("common.pageMeta.defaultDescription");

    useEffect(() => {
        const noindex = !!robots && /noindex/i.test(robots);
        document.title = title || defaultTitle;
        upsertMeta("description", description || defaultDescription);
        if (noindex) removeCanonical();
        else upsertCanonical(ORIGIN + path);
        if (robots) upsertMeta("robots", robots);
        return () => {
            document.title = defaultTitle;
            upsertMeta("description", defaultDescription);
            upsertCanonical(ORIGIN + "/");
            if (robots) upsertMeta("robots", "index,follow");
        };
    }, [title, description, path, robots, defaultTitle, defaultDescription]);
}
